import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Separator } from "../../../components/ui/separator";
import { Badge } from "../../../components/ui/badge";
import { ArrowLeft, Save, Send, AlertCircle, CheckCircle } from "lucide-react";
import API_CONSTRUCTOR from "../../../models/api-model";
import HouseFlippingCalculator from "../../../components/calculator";
import { toast } from "sonner";
import { formatDateTime } from "../../../utils/adminUtils";
import { StageTimeline, StageControls } from "../../../components/workflow";
import { getPropertyField } from "../../../utils/propertyDataHelper";
import {
  getBuildingSquareFeet,
  getListingListPrice,
  resolvePrimaryListing,
} from "../../../utils/property-calculations";
export const CalculationEditor = () => {
  const { queryId, calcId } = useParams();
  const navigate = useNavigate();
  const isEdit = !!calcId;

  const [query, setQuery] = useState(null);
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Initialize API
  const api = API_CONSTRUCTOR();

  // Calculator results state
  const [calculatorResults, setCalculatorResults] = useState(null);
  
  // Market value state for slider
  const [marketValuePerSqft, setMarketValuePerSqft] = useState(400);
  const [propertyData, setPropertyData] = useState(null);
  const [renovationPlanHtml, setRenovationPlanHtml] = useState("");
  const [notesForHomeowner, setNotesForHomeowner] = useState("");
  const [notesForInvestor, setNotesForInvestor] = useState("");
  const [renovationTimeline, setRenovationTimeline] = useState("");

  useEffect(() => {
    loadData();
  }, [queryId, calcId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isEdit) {
        const calcResponse = await api.get(`/calculations/${calcId}`);
        const calc = calcResponse.data;
        setCalculation(calc);
        setRenovationPlanHtml(calc.renovation_plan_html || "");
        setNotesForHomeowner(calc.notes_for_homeowner || "");
        setNotesForInvestor(calc.notes_for_investor || "");
        setRenovationTimeline(calc.renovation_timeline || "");

        const queryResponse = await api.get(`/queries/${calc.query_id}`);
        setQuery(queryResponse.data);
        setMarketValuePerSqft(calc.market_value_per_sqft || 400);
        
        // Set property data from calculation for editing
        const propertyDataFromCalc = {
          squareFeet: calc.home_area,
          estimatedValue: calc.present_value,
          estimatedRentAmount: calc.current_rental,
          loanBalance: calc.loan_amount,
          lotSquareFeet: calc.lot_size,
          pool: calc.has_pool || false,
          pricePerSquareFoot: calc.market_value_per_sqft,
          // Store initial inputs for the calculator to load in edit mode
          // IMPORTANT: Database stores rental_period in MONTHS, calculator expects YEARS
          initialInputs: {
            presentValue: calc.present_value,
            homeArea: calc.home_area,
            loanAmount: calc.loan_amount,
            currentRental: calc.current_rental,
            lotSize: calc.lot_size,
            hasPool: calc.has_pool ? 'yes' : 'no',
            renovationTier: calc.renovation_tier,
            extraAreaTier: calc.cost_per_sqft_extra || 400,
            addAreaCheckbox: (calc.extra_area_added || 0) > 0,
            extraArea: calc.extra_area_added || 0,
            investorType: calc.investor_type,
            rentalPeriod: Math.round(calc.rental_period / 12), // Convert months to years for calculator
            guaranteedReturnPct: calc.guaranteed_return_pct || 15,
            upfrontPaymentPct: calc.upfront_payment_pct || 30,
            paymentParts: calc.payment_parts || 4,
            marketValuePerSqft: calc.market_value_per_sqft,
          }
        };
        setPropertyData(propertyDataFromCalc);
      } else {
        const queryResponse = await api.get(`/queries/${queryId}`);
        setQuery(queryResponse.data);
        
        if (queryResponse.data.property_id) {
          try {
            const propertyResponse = await api.get(`/properties/${queryResponse.data.property_id}`);
            const property = propertyResponse;

            const primaryListing = resolvePrimaryListing(property);
            const mappedData = {
              squareFeet: getBuildingSquareFeet(property),
              estimatedValue:
                getListingListPrice(primaryListing) ?? property.estimatedValue,
              estimatedRentAmount: property.estimatedRentAmount,
              loanBalance: property.loanBalance,
              lotSquareFeet: property.lotSquareFeet,
              pool: property.pool || false,
              pricePerSquareFoot:
                primaryListing?.pricePerSquareFoot ||
                property.pricePerSquareFoot,
            };
            
            setPropertyData(mappedData);
            setMarketValuePerSqft(mappedData.pricePerSquareFoot || 400);
          } catch (propErr) {
            toast.error('Could not load property details');
          }
        }
        
        try {
          const existingCalcResponse = await api.get(`/calculations/query/${queryId}`);
          if (existingCalcResponse.success && existingCalcResponse.data) {
            toast.info('Calculation already exists. Redirecting...');
            setTimeout(() => {
              navigate(`/admin/calculations/${existingCalcResponse.data.id}`, { replace: true });
            }, 1500);
            return;
          }
        } catch (err) {
          // Expected 404 for new calculations
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCalculatorResults = useCallback((results) => {
    setCalculatorResults(results);
  }, []);

  const handleSave = async (shareWithHomeowner = false) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      if (!query || !query.id) {
        setError("Query data not loaded. Please refresh the page.");
        return;
      }

      if (!calculatorResults) {
        setError("Please calculate results before saving");
        return;
      }

      const inputs = calculatorResults.inputs || {};

      if (!inputs.presentValue) {
        setError("Invalid calculation data. Please recalculate.");
        return;
      }

      // Send both calculator inputs AND calculated results to backend
      const payload = {
        queryId: query.id,
        calculatorInputs: inputs,
        calculatorResults: calculatorResults.results,
        renovationPlanDelta: renovationPlanHtml ? { html: renovationPlanHtml } : null,
        renovationTimeline: renovationTimeline || null,
        notesForHomeowner: notesForHomeowner || null,
        notesForInvestor: notesForInvestor || null,
      };

      if (isEdit) {
        await api.put(`/calculations/${calcId}`, payload);
        toast.success("Calculation updated successfully");

        if (shareWithHomeowner) {
          await api.post(`/calculations/${calcId}/share`, {});
          toast.success("Calculation shared with homeowner");
          setTimeout(() => navigate(`/admin/queries/${query.id}`), 1500);
        }
      } else {
        const response = await api.post("/calculations", payload);
        const newCalcId = response.data.id;
        toast.success("Calculation created successfully");

        if (shareWithHomeowner) {
          try {
            await api.post(`/calculations/${newCalcId}/share`, {});
            toast.success("Calculation shared with homeowner");
          } catch (shareErr) {
            toast.error(shareErr.response?.data?.error || "Failed to share");
          }
          setTimeout(() => navigate(`/admin/queries/${query.id}`), 1500);
        } else {
          setTimeout(() => navigate(`/admin/calculations/${newCalcId}`), 1500);
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Failed to save calculation";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  if (!query) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate("/admin/queries")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Queries
        </Button>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Query not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12">
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate(`/admin/queries/${query.id}`)}>
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Query
      </Button>

      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold tracking-tight">
          {isEdit ? 
            (calculation?.status === 'rejected' ? 'Revise Rejected Calculation' : 'Edit Calculation') 
            : 'Create New Calculation'}
        </h2>
        <p className="text-muted-foreground">
          Property: {query.property_address || getPropertyField(query, 'address')}, {query.property_city || getPropertyField(query, 'city')}
        </p>
        <p className="text-sm text-muted-foreground">
          Homeowner: {query.homeowner_name} ({query.homeowner_email})
        </p>
        {isEdit && calculation && (
          <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground">
            <span>
              Status: <Badge className={
                calculation.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                calculation.status === 'shared_with_homeowner' ? 'bg-blue-100 text-blue-800' :
                calculation.status === 'rejected' ? 'bg-red-100 text-red-800' :
                calculation.status === 'approved' ? 'bg-green-100 text-green-800' :
                'bg-gray-100 text-gray-800'
              } variant="secondary">
                {calculation.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </Badge>
            </span>
            {calculation.created_by_name && (
              <span>Created by: {calculation.created_by_name}</span>
            )}
            {calculation.shared_at && (
              <span>Shared: {formatDateTime(calculation.shared_at)}</span>
            )}
          </div>
        )}
      </div>

      {/* Rejected Calculation Banner */}
      {isEdit && calculation?.status === 'rejected' && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            <strong>Homeowner Rejected This Calculation.</strong>
            <br />
            This is the same calculation record - edit it and re-share with the homeowner. 
            Only one calculation exists per property (enforced by system).
          </AlertDescription>
        </Alert>
      )}

      {/* Approved Calculation Warning */}
      {isEdit && calculation?.status === 'approved' && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            <strong>This calculation is approved and immutable.</strong>
            <br />
            Investors may have already committed based on these numbers. Editing is not recommended.
          </AlertDescription>
        </Alert>
      )}

      {/* Success/Error Messages */}
      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Workflow Section */}
      {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <StageTimeline queryId={query.id} />
        <StageControls 
          queryId={query.id}
          currentStage={query.current_stage_key}
          onStageAdvanced={loadData}
        />
      </div> */}

      <Separator />

      {/* Calculator Section */}
      <Card>
        <CardHeader>
          <CardTitle>Property Calculator</CardTitle>
        </CardHeader>
        <CardContent>
          {propertyData ? (
            <HouseFlippingCalculator
              buildingSquareFeet={propertyData.squareFeet}
              estimatedRentAmount={propertyData.estimatedRentAmount}
              estimatedValue={propertyData.estimatedValue}
              loanBalance={propertyData.loanBalance}
              lotSquareFeet={propertyData.lotSquareFeet}
              marketValuePerSqft={marketValuePerSqft}
              setMarketValuePerSqft={setMarketValuePerSqft}
              pool={propertyData.pool}
              initialInputs={propertyData.initialInputs}
              onCalculate={handleCalculatorResults}
            />
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              Loading property data...
            </div>
          )}
        </CardContent>
      </Card>

      <Separator />

      {/* Renovation Plan Section */}
      <Card>
        <CardHeader>
          <CardTitle>Renovation Plan</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="renovation-plan">Detailed Renovation Plan</Label>
            <p className="text-sm text-muted-foreground">
              Describe the renovation plan, materials, timeline, and special considerations.
            </p>
            <Textarea
              id="renovation-plan"
              placeholder="Enter detailed renovation plan..."
              value={renovationPlanHtml}
              onChange={(e) => setRenovationPlanHtml(e.target.value)}
              rows={10}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="renovation-timeline">Renovation Timeline</Label>
            <Input
              id="renovation-timeline"
              placeholder="e.g., 12 months, 6-8 weeks, etc."
              value={renovationTimeline}
              onChange={(e) => setRenovationTimeline(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Admin Notes Section */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Notes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="notes-homeowner">Notes for Homeowner</Label>
            <Textarea
              id="notes-homeowner"
              placeholder="Any additional notes or instructions for the homeowner..."
              value={notesForHomeowner}
              onChange={(e) => setNotesForHomeowner(e.target.value)}
              rows={4}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes-investor">Notes for Investors</Label>
            <Textarea
              id="notes-investor"
              placeholder="Any additional information for potential investors..."
              value={notesForInvestor}
              onChange={(e) => setNotesForInvestor(e.target.value)}
              rows={4}
            />
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => handleSave(false)}
              disabled={saving || !calculatorResults}
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save as Draft"}
            </Button>
            <Button
              onClick={() => handleSave(true)}
              disabled={saving || !calculatorResults}
            >
              <Send className="w-4 h-4 mr-2" />
              {saving ? "Sharing..." : "Share with Homeowner"}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Save as Draft: Not shared with homeowner. Share: Saved and homeowner notified.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default CalculationEditor;

